import { useState } from 'react'
import { Search, Plus, Edit2, Trash2, AlertTriangle, Loader2, ChevronLeft, ChevronRight, X, ExternalLink, Calendar, CheckCircle } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useRecalls, useRecallMutations, useBrandSearch, Recall } from '@/lib/data'

interface RecallsViewProps {
  client: SupabaseClient | null
}

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-green-500/10 text-green-400' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-500/10 text-amber-400' },
  { value: 'high', label: 'High', color: 'bg-orange-500/10 text-orange-400' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500/10 text-red-400 animate-pulse' },
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'resolved', label: 'Resolved', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'expired', label: 'Expired', color: 'bg-gray-500/20 text-gray-400' },
]

const SOURCE_AGENCIES = ['FDA', 'USDA', 'CPSC', 'Other'] as const

export function RecallsView({ client }: RecallsViewProps) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<'active' | 'resolved' | 'expired' | ''>('')
  const [severityFilter, setSeverityFilter] = useState<'low' | 'medium' | 'high' | 'critical' | ''>('')
  const [showModal, setShowModal] = useState(false)
  const [editingRecall, setEditingRecall] = useState<Recall | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<Recall | null>(null)
  const limit = 20

  const { recalls, total, isLoading, error, refetch } = useRecalls(client, {
    page,
    limit,
    search,
    status: statusFilter || undefined,
    severity: severityFilter || undefined,
  })

  const { createRecall, updateRecall, deleteRecall, isLoading: mutationLoading, error: mutationError } = useRecallMutations(client)

  const totalPages = Math.ceil(total / limit)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const handleSave = async (formData: Partial<Recall>) => {
    let result
    if (editingRecall) {
      result = await updateRecall(editingRecall.id, formData)
    } else {
      result = await createRecall(formData)
    }

    // Only close panel and refetch if successful
    if (result) {
      setShowModal(false)
      setEditingRecall(null)
      refetch()
    }
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await deleteRecall(deleteConfirm.id)
      setDeleteConfirm(null)
      refetch()
    }
  }

  const getSeverityStyle = (severity: string) => {
    return SEVERITY_OPTIONS.find(s => s.value === severity)?.color || 'bg-[var(--bg-element)] text-[var(--text-muted)]'
  }

  const getStatusStyle = (status: string) => {
    return STATUS_OPTIONS.find(s => s.value === status)?.color || 'bg-[var(--bg-element)] text-[var(--text-muted)]'
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <AlertTriangle size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view recalls</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">Recalls Management</h2>
        <button
          onClick={() => {
            setEditingRecall(null)
            setShowModal(true)
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90"
        >
          <Plus size={16} /> Add Recall
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" size={18} />
          <input
            type="text"
            placeholder="Search recalls..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
          />
        </form>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as typeof statusFilter)
            setPage(1)
          }}
          className="bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>

        <select
          value={severityFilter}
          onChange={(e) => {
            setSeverityFilter(e.target.value as typeof severityFilter)
            setPage(1)
          }}
          className="bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
        >
          <option value="">All Severities</option>
          {SEVERITY_OPTIONS.map(severity => (
            <option key={severity.value} value={severity.value}>{severity.label}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : recalls.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-muted)]">
            No recalls found
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Title</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Brand</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Severity</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Status</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Recall Date</th>
                <th className="text-left p-4 text-[var(--text-muted)] font-medium text-sm">Published</th>
                <th className="text-right p-4 text-[var(--text-muted)] font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recalls.map((recall) => (
                <tr key={recall.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-element)]">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[var(--bg-element)] flex items-center justify-center">
                        <AlertTriangle size={16} className={
                          recall.severity === 'critical' ? 'text-red-400' :
                          recall.severity === 'high' ? 'text-orange-400' :
                          recall.severity === 'medium' ? 'text-amber-400' : 'text-green-400'
                        } />
                      </div>
                      <div>
                        <span className="text-[var(--text-main)] font-medium block">{recall.title}</span>
                        {recall.subtitle && (
                          <span className="text-[var(--text-muted)] text-xs">{recall.subtitle}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-[var(--text-muted)]">
                    {recall.brand_name || '-'}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full capitalize ${getSeverityStyle(recall.severity)}`}>
                      {recall.severity}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusStyle(recall.status)}`}>
                      {recall.status}
                    </span>
                  </td>
                  <td className="p-4 text-[var(--text-muted)]">
                    {recall.recall_date ? new Date(recall.recall_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-4">
                    {recall.is_published ? (
                      <span className="text-emerald-400 text-sm flex items-center gap-1">
                        <CheckCircle size={14} /> Published
                      </span>
                    ) : (
                      <span className="text-[var(--text-muted)] text-sm">Draft</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingRecall(recall)
                          setShowModal(true)
                        }}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)] rounded-lg"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(recall)}
                        className="p-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--text-muted)] text-sm">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} recalls
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronLeft size={16} className="text-[var(--text-muted)]" />
            </button>
            <span className="text-[var(--text-main)] px-4">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 bg-[var(--bg-element)] border border-[var(--border)] rounded-lg disabled:opacity-50"
            >
              <ChevronRight size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>
      )}

      {/* Edit/Create Panel */}
      {showModal && (
        <RecallModal
          recall={editingRecall}
          client={client}
          onClose={() => {
            setShowModal(false)
            setEditingRecall(null)
          }}
          onSave={handleSave}
          isLoading={mutationLoading}
          error={mutationError}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-[var(--text-main)] mb-2">Delete Recall</h3>
            <p className="text-[var(--text-muted)] mb-6">
              Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)]"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutationLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {mutationLoading && <Loader2 size={16} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface RecallModalProps {
  recall: Recall | null
  client: SupabaseClient
  onClose: () => void
  onSave: (data: Partial<Recall>) => void
  isLoading: boolean
  error: string | null
}

function RecallModal({ recall, client, onClose, onSave, isLoading, error }: RecallModalProps) {
  const [brandSearch, setBrandSearch] = useState('')
  const [showBrandDropdown, setShowBrandDropdown] = useState(false)
  const { brands, isLoading: brandsLoading } = useBrandSearch(client, brandSearch)

  const [formData, setFormData] = useState({
    brand_id: recall?.brand_id || '',
    brand_name: recall?.brand_name || '',
    ownership_type: recall?.ownership_type || '',
    title: recall?.title || '',
    subtitle: recall?.subtitle || '',
    par1: recall?.par1 || '',
    par2: recall?.par2 || '',
    bullets: recall?.bullets || ['', '', '', ''],
    severity: recall?.severity || 'medium',
    recall_reason: recall?.recall_reason || '',
    affected_products: recall?.affected_products || '',
    source_agency: recall?.source_agency || '',
    source_url: recall?.source_url || '',
    recall_date: recall?.recall_date?.split('T')[0] || '',
    expiration_date: recall?.expiration_date?.split('T')[0] || '',
    status: recall?.status || 'active',
    is_published: recall?.is_published || false,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const submitData: Partial<Recall> = {
      brand_id: formData.brand_id || null,
      brand_name: formData.brand_name,
      ownership_type: formData.ownership_type || null,
      title: formData.title,
      subtitle: formData.subtitle || null,
      par1: formData.par1,
      par2: formData.par2 || null,
      bullets: formData.bullets.filter(b => b.trim()) || null,
      severity: formData.severity as Recall['severity'],
      recall_reason: formData.recall_reason || null,
      affected_products: formData.affected_products || null,
      source_agency: (formData.source_agency || null) as Recall['source_agency'],
      source_url: formData.source_url || null,
      recall_date: formData.recall_date,
      expiration_date: formData.expiration_date || null,
      status: formData.status as Recall['status'],
      is_published: formData.is_published,
    }

    // Set published_at if publishing for the first time
    if (formData.is_published && !recall?.is_published) {
      submitData.published_at = new Date().toISOString()
    }

    onSave(submitData)
  }

  const selectBrand = (brand: { id: string; brand_name: string; ownership_type: string | null; type?: 'brand' | 'company' }) => {
    setFormData(prev => ({
      ...prev,
      brand_id: brand.id,
      brand_name: brand.brand_name,
      ownership_type: brand.ownership_type || '',
    }))
    setBrandSearch('')
    setShowBrandDropdown(false)
  }

  const addBullet = () => {
    setFormData(prev => ({ ...prev, bullets: [...prev.bullets, ''] }))
  }

  const removeBullet = (index: number) => {
    setFormData(prev => ({
      ...prev,
      bullets: prev.bullets.filter((_, i) => i !== index),
    }))
  }

  const updateBullet = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      bullets: prev.bullets.map((b, i) => i === index ? value : b),
    }))
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-out Panel */}
      <div className="fixed inset-y-0 right-0 w-[60%] max-w-3xl bg-[var(--bg-card)] border-l border-[var(--border)] z-50 shadow-2xl animate-slide-in-right flex flex-col">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between shrink-0">
          <h2 className="text-xl font-bold text-[var(--text-main)]">
            {recall ? 'Edit Recall' : 'Add Recall'}
          </h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-main)] p-2 hover:bg-[var(--bg-element)] rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Error Display */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
              <p className="font-medium">Error saving recall</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Brand Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Brand *</label>
            <input
              type="text"
              value={brandSearch || formData.brand_name}
              onChange={(e) => {
                setBrandSearch(e.target.value)
                setShowBrandDropdown(true)
                if (!e.target.value) {
                  setFormData(prev => ({ ...prev, brand_id: '', brand_name: '', ownership_type: '' }))
                }
              }}
              onFocus={() => brandSearch && setShowBrandDropdown(true)}
              placeholder="Search for a brand or company..."
              required
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
            {showBrandDropdown && brandSearch && (
              <div className="absolute z-10 w-full mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-xl max-h-48 overflow-y-auto">
                {brandsLoading ? (
                  <div className="p-3 text-center text-[var(--text-muted)]">
                    <Loader2 size={16} className="animate-spin inline mr-2" />
                    Searching...
                  </div>
                ) : brands.length === 0 ? (
                  <div className="p-3 text-center text-[var(--text-muted)]">
                    No brands or companies found
                  </div>
                ) : (
                  brands.map(brand => (
                    <button
                      key={`${brand.type}-${brand.id}`}
                      type="button"
                      onClick={() => selectBrand(brand)}
                      className="w-full px-4 py-2 text-left hover:bg-[var(--bg-element)] text-[var(--text-main)]"
                    >
                      <span className="font-medium">{brand.brand_name}</span>
                      <span className={`text-xs ml-2 px-1.5 py-0.5 rounded ${
                        brand.type === 'company' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {brand.type === 'company' ? 'Company' : 'Brand'}
                      </span>
                      {brand.ownership_type && (
                        <span className="text-[var(--text-muted)] text-xs ml-2">({brand.ownership_type})</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
            {formData.brand_name && formData.ownership_type && (
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Ownership: {formData.ownership_type}
              </p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              placeholder="e.g., Listeria Found in ABC Products"
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Subtitle</label>
            <input
              type="text"
              value={formData.subtitle}
              onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
              placeholder="Optional tagline under title"
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          {/* Paragraph 1 */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Paragraph 1 * (Markdown supported)</label>
            <textarea
              value={formData.par1}
              onChange={(e) => setFormData(prev => ({ ...prev, par1: e.target.value }))}
              required
              rows={4}
              placeholder="Main description of the recall..."
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
            />
          </div>

          {/* Paragraph 2 */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Paragraph 2 (Markdown supported)</label>
            <textarea
              value={formData.par2}
              onChange={(e) => setFormData(prev => ({ ...prev, par2: e.target.value }))}
              rows={3}
              placeholder="Extended details..."
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
            />
          </div>

          {/* Key Points */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Key Points</label>
            <div className="space-y-2">
              {formData.bullets.map((bullet, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={bullet}
                    onChange={(e) => updateBullet(index, e.target.value)}
                    placeholder={`Point ${index + 1}`}
                    className="flex-1 bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                  />
                  {formData.bullets.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBullet(index)}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addBullet}
                className="text-[var(--primary)] text-sm hover:underline"
              >
                + Add another point
              </button>
            </div>
          </div>

          {/* Severity & Status Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Severity *</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value as 'low' | 'medium' | 'high' | 'critical' }))}
                required
                className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
              >
                {SEVERITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'active' | 'resolved' | 'expired' }))}
                required
                className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Recall Reason */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Recall Reason</label>
            <input
              type="text"
              value={formData.recall_reason}
              onChange={(e) => setFormData(prev => ({ ...prev, recall_reason: e.target.value }))}
              placeholder="e.g., Potential Listeria monocytogenes contamination"
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
            />
          </div>

          {/* Affected Products */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Affected Products (Markdown supported)</label>
            <textarea
              value={formData.affected_products}
              onChange={(e) => setFormData(prev => ({ ...prev, affected_products: e.target.value }))}
              rows={3}
              placeholder="- Product Name 16oz (UPC: 123456789012)&#10;- Product Name 24oz (UPC: 123456789013)"
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
            />
          </div>

          {/* Source Agency & URL Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Source Agency</label>
              <select
                value={formData.source_agency}
                onChange={(e) => setFormData(prev => ({ ...prev, source_agency: e.target.value }))}
                className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
              >
                <option value="">Select agency...</option>
                {SOURCE_AGENCIES.map(agency => (
                  <option key={agency} value={agency}>{agency}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Source URL</label>
              <input
                type="url"
                value={formData.source_url}
                onChange={(e) => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
                placeholder="https://..."
                className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
              />
            </div>
          </div>

          {/* Date Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Recall Date *</label>
              <input
                type="date"
                value={formData.recall_date}
                onChange={(e) => setFormData(prev => ({ ...prev, recall_date: e.target.value }))}
                required
                className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-main)] mb-2">Expiration Date</label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
              />
            </div>
          </div>

          {/* Published Toggle */}
          <div className="flex items-center gap-3 p-4 bg-[var(--bg-element)] rounded-lg">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
              className="w-5 h-5 rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--primary)]"
            />
            <label htmlFor="is_published" className="text-[var(--text-main)] font-medium">
              Publish this recall
            </label>
            <span className="text-[var(--text-muted)] text-sm">
              {formData.is_published ? 'Visible in the app' : 'Saved as draft'}
            </span>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border)]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.brand_name || !formData.title || !formData.par1 || !formData.recall_date}
              className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading && <Loader2 size={16} className="animate-spin" />}
              {recall ? 'Update Recall' : 'Create Recall'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
